import React from "react";

const StepByStepOverview = () => {
  return (
    <React.Fragment>
      <h1>Publication Process Overview</h1>
      <p>
        The time required for publication may vary in length due to article complexity, length, and
        availability of reviewers with sufficient domain knowledge. CJCS strives to publish articles
        within eight weeks of submission.
      </p>
      <p>Submission of a manuscript implies the following: </p>
      <ul className="disc indented-for-medium-up">
        <li>
          The work described has not been published before and is not under consideration for
          publication elsewhere.
        </li>
        <li>
          Its publication has been approved by all co-authors (if any) and the responsible
          authorities—tacitly or explicitly—at the institution where the work was conducted.
        </li>
        <li>
          The research complies with research integrity standards, including approval from relevant
          ethics committees for studies involving human or animal subjects.
        </li>
        <li>The authors have disclosed any potential conflicts of interest.</li>
        <li>
          Data supporting the findings are available as per journal guidelines, or a legitimate
          statement regarding data non-availability has been provided.
        </li>
        <li>
          The manuscript will be published under an open access license, and authors retain
          copyright while granting the publisher a license to distribute the work.
        </li>
        <li>
          The publisher will not be held legally responsible should there be any claims for
          compensation.
        </li>
      </ul>
      <h2>
        <strong>Permissions</strong>
      </h2>
      <p>
        During manuscript submission, authors wishing to include figures, tables, or text passages
        that have already been published elsewhere are required to obtain the necessary
        permission(s) from the copyright owner(s) for both the print and online format and include
        evidence that such permission has been granted. Any material received without such evidence
        will be assumed to originate from the authors.
      </p>
      <p>
        All articles must be submitted through our online system. Emailed submissions will be
        discarded without editorial review. If you encounter technical issues during the submission
        process, please email{" "}
        <a href="mailto:cureus-support@springernature.com">cureus-support@springernature.com</a>.
      </p>
      <div role="list" className="big-numbers">
        <div className="step-1">
          <p>
            The submitting author provides all article details, including co-authors' names and
            affiliations, and uploads the article text along with any relevant media. The submitting
            author must disclose any conflicts of interest and adhere to applicable ethics
            statements. Lastly, the author enters contact information for at least five potential
            reviewers. (View our comprehensive{" "}
            <a
              href="/journal/computer-science/guide#!/author-instructions/author-roles"
              target="_blank"
              rel="noopener"
            >
              Author Instructions
            </a>{" "}
            for a more detailed look at the submission process).
          </p>
        </div>

        <div className="step-2">
          <p>
            The author submits the article for Editor Check, a preliminary review by the CJCS
            editorial team. Based on this review, the article is directed down one of the following
            paths:
          </p>
          <h2>
            <strong>Approved for Peer Review</strong>
          </h2>
          <p>
            If all CJCS editorial requirements have been met, the article will be approved for peer
            review with no editing fees required.
          </p>

          <h2>
            <strong>Preferred Editing Purchase Required</strong>
          </h2>
          <p>
            Review articles and submissions containing multiple formatting and/or language errors
            will require the purchase of our{" "}
            <a href="/cureus_editing_services" target="_blank" rel="noopener">
              Preferred Editing Service
            </a>{" "}
            to proceed to peer review.
          </p>

          <h2>
            <strong>Third-Party Editing Service Required</strong>
          </h2>
          <p>
            If severe language issues are present (incorrect language and grammar, poor formatting
            and structure, missing flow, ambiguous wording, usage of scientifically inaccurate
            terminologies, incorrect units, failure to spell out non-standard abbreviations at first
            mention, etc.), language editing from a third-party service such as{" "}
            <a href="https://www.aje.com/go/springernature/" target="_blank" rel="noopener">
              American Journal Experts (AJE)
            </a>
            , etc. will be required before resubmission.
          </p>

          <h2>
            <strong>Deferred for Revisions</strong>
          </h2>
          <p>
            The following issues will require correction by the author before our editorial review
            can proceed. If these errors are not fixed or other errors are found (references,
            subheadings, spelling, grammar, punctuation, etc.), the article may still require{" "}
            <a href="/cureus_editing_services">Preferred Editing</a>.
          </p>
          <ul className="disc">
            <li>Scientific/study-related revisions needed</li>
            <li>Too much text is duplicated from cited sources</li>
            <li>
              Tables that are incomprehensible, contain multiple values in a single cell, or are
              submitted as figures
            </li>
            <li>Missing media items</li>
            <li>Videos that do not play </li>
            <li>Visible identifying participant information </li>
            <li>Figures that are blurry, stretched, skewed, too dark or that require cropping</li>
          </ul>

          <h2>
            <strong>Desk Rejected</strong>
          </h2>
          <p>
            The article is outside the scope of the journal, not applicable to the scientific
            community or is otherwise ill-suited for publication (e.g. faulty study design).
          </p>

          <h2>
            <strong>Blocked</strong>
          </h2>
          <p>
            The article is in direct violation of our submission policies (e.g. plagiarism, academic
            fraud, attempts to circumvent peer or editorial review). Submitting authors of articles
            blocked for plagiarism or fraud will be permanently banned from CJCS.
          </p>
        </div>

        <div className="step-3">
          <h2>
            <strong>Peer review process:</strong>
          </h2>
          <p>
            After preliminary review by in-house associate editors, the manuscript is approved for
            single-blind peer review after the following has been confirmed:
          </p>
          <ul>
            <li className="check">
              <strong>All CJCS editorial requirements have been addressed.</strong>
            </li>
            <li className="check">
              <strong>Preferred Editing Service has been purchased, if required.</strong>
            </li>
            <li className="check">
              <strong>
                Professional third-party language editing services have been utilized, if required.
              </strong>
            </li>
          </ul>
          <p>
            Upon approval for peer review, invitations are emailed to the author-suggested reviewers
            as well as six reviewers automatically invited by CJCS. During peer review, the author
            can invite new reviewers and send reminders to previously invited reviewers via the
            author dashboard. However, the names of the peer reviewers get converted into Greek
            letters and remain anonymized for authors.
          </p>
        </div>

        <div className="step-4">
          <p>
            The article is unlocked for editing when two CJCS-invited reviews have been submitted.
            The author may now choose to:
          </p>
          <ul className="letter">
            <li>
              Wait for additional reviews (all article versions are saved and available to the
              author and reviewers) before revising, responding to each reviewer's comment, and
              submitting for publication approval (Editor Approval) while also explaining the
              revisions.
            </li>
            <li>
              Address all peer reviewers' comments, irrespective of whether they are from
              author-invited or CJCS-invited peer reviewers.
            </li>
            <li>
              Make revisions and request that the article be re-reviewed by one or more reviewers
              who completed a review during the first round of review.
            </li>
            <li>
              Make revisions, respond to each reviewer's comment, and submit for publication
              approval while also explaining the revisions.
            </li>
          </ul>
        </div>
        <p>
          Authors can end the peer review process for all reviewers once their article has met our
          requirements. If this option is chosen, all outstanding and in-progress reviews will be
          terminated, except for any reviews that were started within the previous 48 hours. These
          reviewers are given a maximum of 24 hours to submit their review.
        </p>
        <p>
          If authors chose to end the review, and the peer reviewer has already claimed an article
          to review in the past 48 hours, then the reviewers will be notified that they have a
          maximum of 24 hours remaining to submit their review since two CJCS-invited peer reviewers
          have already completed their review.
        </p>

        <div className="step-5">
          <p>
            Once the article has been submitted for Editor Approval, an associate editor will
            conduct a final editorial review to confirm the article’s suitability for publication
            and takes a final decision on the manuscript. This includes ensuring that all reviewer
            comments have been acknowledged and addressed appropriately.
          </p>
        </div>

        <div className="step-6">
          <p>
            If the revised manuscript does not adequately reflect reviewer feedback, the associate
            editor will likely defer the article's publication. The author is then notified that
            further revision is required. Further, an associate editor can also send the article
            back to the peer reviewer after revisions for re-review.
          </p>
        </div>

        <p>
          If a completed peer review is determined to be illegitimate, the review will be rejected
          and the article returned to peer review (if the article no longer satisfies CJCS review
          requirements after the rejection).
        </p>

        <div className="step-7">
          <p>
            After the associate editor has completed their review, the article will enter the
            copy-editing queue. Articles that have not undergone preferred editing may be deferred
            back to the submitting author for formatting and language revisions prior to the start
            of copy editing. The copy editor may also choose to email any related queries directly
            to the submitting author. Once the article is approved for publication and copyediting
            has been completed, the article is sent back to the author for final proofreading. The
            author is notified that they can now sign in and publish the article from their
            dashboard.
          </p>
        </div>
      </div>
    </React.Fragment>
  );
};

export default StepByStepOverview;
